import { useEffect, useRef, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import useViewport from '../../../../../../hooks/useViewport'
import { useTheme } from '@homebotapp/hb-react-component-catalog'
import { formatCurrency } from '../../../../../../helpers/formatters'
import { Cell, Label, Pie, PieChart, ResponsiveContainer, Sector } from 'recharts'

export const MSG = defineMessages({
  principalAndInterest: {
    id: 'EstimatedMonthlyPaymentBreakdownDonutChart.principalAndInterest',
    defaultMessage: 'Principal & Interest'
  },
  secondMortgagePrincipalAndInterest: {
    id: 'EstimatedMonthlyPaymentBreakdownDonutChart.secondMortgagePrincipalAndInterest',
    defaultMessage: 'Principal & Interest'
  },
  mortgageInsurance: {
    id: 'EstimatedMonthlyPaymentBreakdownDonutChart.mortgageInsurance',
    defaultMessage: 'Mortgage Insurance'
  },
  propertyTaxes: {
    id: 'EstimatedMonthlyPaymentBreakdownDonutChart.propertyTaxes',
    defaultMessage: 'Property Taxes'
  },
  homeInsurance: {
    id: 'EstimatedMonthlyPaymentBreakdownDonutChart.homeInsurance',
    defaultMessage: 'Home Insurance'
  },
  hoaFees: {
    id: 'EstimatedMonthlyPaymentBreakdownDonutChart.hoaFees',
    defaultMessage: 'HOA Fees'
  },
  perMonth: {
    id: 'EstimatedMonthlyPaymentBreakdownDonutChart.pmt',
    defaultMessage: '/ month'
  },
  utilities: {
    id: 'EstimatedMonthlyPaymentBreakdownDonutChart.utilities',
    defaultMessage: 'Utilities'
  }
})

export interface EstimatedMonthlyPaymentBreakdownDonutChartProps {
  hoaFees: string
  utilities: number
  propertyTaxes: string
  homeInsurance: string
  assumableToggle: boolean
  mortgageInsurance: string
  principalAndInterest: string
  totalMonthlyHomeExpenses: string
  secondMortgagePrincipalAndInterest: string
}

export const EstimatedMonthlyPaymentBreakdownDonutChart = ({
  hoaFees,
  utilities,
  propertyTaxes,
  homeInsurance,
  assumableToggle,
  mortgageInsurance,
  principalAndInterest,
  totalMonthlyHomeExpenses,
  secondMortgagePrincipalAndInterest
}: EstimatedMonthlyPaymentBreakdownDonutChartProps) => {
  const intl = useIntl()
  const { theme } = useTheme()
  const touchedRef = useRef(false)
  const [data, setData] = useState([])
  const { isMediumOrAbove } = useViewport()
  const [activeIndex, setActiveIndex] = useState(-1)

  const getData = () => {
    const values = []

    if (principalAndInterest && principalAndInterest !== '0') {
      values.push({
        value: parseFloat(principalAndInterest),
        color: theme.tokens[theme.isLightThemeType ? 'brand-success-800' : 'brand-success-500'],
        name: intl.formatMessage(MSG.principalAndInterest)
      })
    }

    if (secondMortgagePrincipalAndInterest && assumableToggle && secondMortgagePrincipalAndInterest !== '0') {
      // @TODO:DESIGNSYSTEM - add yellow ramp
      values.push({
        color: theme.tokens[theme.isLightThemeType ? 'brand-success-700' : 'brand-success-700'],
        value: parseFloat(secondMortgagePrincipalAndInterest),
        name: intl.formatMessage(MSG.secondMortgagePrincipalAndInterest)
      })
    }

    if (utilities) {
      // @TODO:DESIGNSYSTEM - add yellow ramp
      values.push({
        color: '#E7C546',
        value: utilities,
        name: intl.formatMessage(MSG.utilities)
      })
    }

    if (mortgageInsurance && mortgageInsurance !== '0') {
      values.push({
        value: parseFloat(mortgageInsurance),
        color: theme.tokens[theme.isLightThemeType ? 'brand-secondary-700' : 'brand-secondary-600'],
        name: intl.formatMessage(MSG.mortgageInsurance)
      })
    }

    if (propertyTaxes && propertyTaxes !== '0') {
      // @TODO:DESIGNSYSTEM - add purple ramp
      values.push({
        color: '#9770F7',
        value: parseFloat(propertyTaxes),
        name: intl.formatMessage(MSG.propertyTaxes)
      })
    }

    if (homeInsurance && homeInsurance !== '0') {
      values.push({
        value: parseFloat(homeInsurance),
        color: theme.tokens[theme.isLightThemeType ? 'brand-warning-700' : 'brand-warning-600'],
        name: intl.formatMessage(MSG.homeInsurance)
      })
    }

    if (hoaFees && hoaFees !== '0') {
      values.push({
        value: parseFloat(hoaFees),
        color: theme.tokens['brand-secondary-900'],
        name: intl.formatMessage(MSG.hoaFees)
      })
    }

    return values
  }

  useEffect(() => {
    setData(getData())
  }, [
    hoaFees,
    utilities,
    propertyTaxes,
    homeInsurance,
    assumableToggle,
    mortgageInsurance,
    principalAndInterest,
    secondMortgagePrincipalAndInterest
  ])

  const renderActiveShape = props => {
    const RADIAN = Math.PI / 180
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, value, name } = props

    const sin = Math.sin(-RADIAN * midAngle)
    const cos = Math.cos(-RADIAN * midAngle)
    const sx = cx + (outerRadius + 10) * cos
    const sy = cy + (outerRadius + 10) * sin
    const mx = cx + (outerRadius + 15) * cos
    const my = cy + (outerRadius + 25) * sin
    const ex = mx + (cos >= 0 ? 1 : -1) * 10
    const textAnchor = cos >= 0 ? 'start' : 'end'

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          tabIndex={-1}
          cornerRadius={2}
          endAngle={endAngle}
          startAngle={startAngle}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          style={{ outline: 'none' }}
          fill={data[activeIndex].color}
        />
        <Sector
          cx={cx}
          cy={cy}
          tabIndex={-1}
          cornerRadius={2}
          endAngle={endAngle}
          startAngle={startAngle}
          style={{ outline: 'none' }}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={data[activeIndex].color}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${my}`} stroke={fill} fill='none' />
        <circle cx={ex} cy={my} r={2} fill={fill} stroke='none' />
        <text
          y={my}
          x={ex + (cos >= 0 ? 1 : -1) * 10}
          fontWeight={800}
          textAnchor={textAnchor}
          fill={theme.tokens['brand-neutral-cool-900']}
        >
          {formatCurrency(value)}
        </text>
        <text
          y={my - 3}
          x={ex + (cos >= 0 ? 1 : -1) * 10}
          dy={18}
          fontSize='12px'
          fontWeight={600}
          textAnchor={textAnchor}
          fill={theme.tokens['brand-neutral-cool-900']}
          style={{ inlineSize: '50px', overflowWrap: 'break-word' }}
        >
          {name}
        </text>
      </g>
    )
  }

  const showingTotalAmountPerMonth = isMediumOrAbove || activeIndex === -1

  return (
    <ResponsiveContainer width='100%' height={295}>
      <PieChart>
        <Pie
          cx='50%'
          cy='50%'
          data={data}
          stroke='none'
          tabIndex={-1}
          dataKey='value'
          paddingAngle={2}
          cornerRadius={2}
          activeIndex={activeIndex}
          style={{ outline: 'none' }}
          innerRadius={isMediumOrAbove ? 82 : 90}
          outerRadius={isMediumOrAbove ? 105 : 115}
          activeShape={isMediumOrAbove && renderActiveShape}
          onMouseEnter={(_, index: number) => !touchedRef.current && setActiveIndex(index)}
          onMouseLeave={() => {
            touchedRef.current = false
            setActiveIndex(-1)
          }}
          onTouchStart={(_, index: number) => {
            touchedRef.current = true
            setActiveIndex(activeIndex === index ? -1 : index)
          }}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} tabIndex={-1} style={{ outline: 'none' }} />
          ))}
          <Label
            fontSize={24}
            position='center'
            fontWeight='bold'
            fill={showingTotalAmountPerMonth ? theme.tokens['brand-primary-600'] : data[activeIndex]?.color}
            value={formatCurrency(
              showingTotalAmountPerMonth ? Number(totalMonthlyHomeExpenses) : data[activeIndex]?.value
            )}
          />
          <Label
            height={20}
            fontSize={15}
            position='centerTop'
            style={{ transform: 'translateY(18px)' }}
            fontWeight={showingTotalAmountPerMonth ? 400 : 600}
            value={showingTotalAmountPerMonth ? intl.formatMessage(MSG.perMonth) : data[activeIndex]?.name}
            fill={theme.tokens[showingTotalAmountPerMonth ? 'brand-primary-600' : 'brand-neutral-cool-900']}
          />
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  )
}
